import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import {
  createInvitation,
  fetchTenants,
  listInvitations,
  resendInvitation,
  revokeInvitation,
} from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { TenantCombobox } from "@/components/TenantCombobox";
import { useAuth } from "@/hooks/useAuth";
import { formatDate, formatDateTime } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { Invitation, InvitationCreated, Role, Scope } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

const ROLES: Role[] = ["admin", "operator", "viewer"];

export function InvitationsAdmin(): JSX.Element {
  usePageTitle("Invitations");
  const auth = useAuth();
  const [rows, setRows] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<InvitationCreated | null>(null);

  async function reload(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await listInvitations());
      setLastFetched(new Date().toISOString());
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onRevoke(id: string, email: string): Promise<void> {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revoke the invitation for ${email}? The link stops working immediately.`)) return;
    setActionError(null);
    try {
      await revokeInvitation(id);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Revoke failed");
    }
  }

  async function onResend(id: string, email: string): Promise<void> {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Resend the invitation to ${email}? The previous link stops working.`)) return;
    setActionError(null);
    try {
      const created = await resendInvitation(id);
      setLastCreated(created);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Resend failed");
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Invitations</h1>
        <p className="text-sm text-ink-300">
          Send a single-use link to a new {auth.scope === "platform" ? "admin or tenant user" : "team member"}.
        </p>
      </header>

      <CreateForm onCreated={(inv) => { setLastCreated(inv); void reload(); }} />

      {lastCreated && (
        <CreatedBanner created={lastCreated} onDismiss={() => setLastCreated(null)} />
      )}

      <section className="card">
        <h2 className="card-title">All invitations</h2>
        {actionError && (
          <p className="text-sm text-severity-high mb-2" role="alert">
            {actionError}
          </p>
        )}
        {loading ? (
          <div className="skeleton h-32" aria-label="Loading invitations" />
        ) : loadError ? (
          <p className="text-sm text-severity-high">{loadError}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-300">No invitations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink-300 border-b border-ink-700">
                <tr>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">
                    Scope / Role
                    <InfoTip
                      label="Scope and role"
                      body="Scope = platform (all tenants) or tenant (one utility). Role = admin (manage users and settings), operator (edit data), or viewer (read-only)."
                    />
                  </th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Expires</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 pr-4">{r.email}</td>
                    <td className="py-2 pr-4 text-ink-300">{r.scope} / {r.role}</td>
                    <td className="py-2 pr-4">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-2 pr-4 text-ink-300">{formatDate(r.expires_at)}</td>
                    <td className="py-2 pr-4 text-ink-300">{formatDate(r.created_at)}</td>
                    <td className="py-2">
                      {r.status === "pending" || r.status === "expired" ? (
                        <span className="inline-flex gap-2">
                          {r.status === "pending" && (
                            <button
                              type="button"
                              onClick={() => void onRevoke(r.id, r.email)}
                              className="btn-primary py-1 px-2 text-xs"
                            >
                              Revoke
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void onResend(r.id, r.email)}
                            className="btn-primary py-1 px-2 text-xs"
                            title="Rotate the link and email it again — the old link stops working"
                          >
                            Resend
                          </button>
                        </span>
                      ) : (
                        <span className="text-ink-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-ink-400">Last updated {lastFetched ? formatDateTime(lastFetched) : "—"}</p>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status }: { status: Invitation["status"] }): JSX.Element {
  const cls =
    status === "pending" ? "pill-low"
    : status === "accepted" ? "pill-ok"
    : status === "revoked" ? "pill-medium"
    : "pill-high";
  return <span className={cls}>{status}</span>;
}

interface CreateFormProps {
  onCreated: (inv: InvitationCreated) => void;
}

function CreateForm({ onCreated }: CreateFormProps): JSX.Element {
  const auth = useAuth();
  const isPlatformAdmin = auth.scope === "platform";
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [scope, setScope] = useState<Scope>(isPlatformAdmin ? "platform" : "tenant");
  const [tenantId, setTenantId] = useState<string>(auth.tenantId ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform admins inviting a tenant user pick from a dropdown of real
  // tenants — typing a raw UUID is brittle and was the obvious UX gap.
  // Tenant admins never see this query (they're locked to their own tenant).
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: fetchTenants,
    enabled: isPlatformAdmin,
    staleTime: 5 * 60 * 1000,
  });

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (scope === "tenant" && !tenantId) {
      setError("Please select a tenant.");
      return;
    }
    setPending(true);
    try {
      const created = await createInvitation({
        email,
        scope,
        role,
        tenant_id: scope === "tenant" ? tenantId : null,
      });
      onCreated(created);
      setEmail("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create invitation");
    } finally {
      setPending(false);
    }
  }

  const tenants = tenantsQuery.data ?? [];

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="card space-y-4">
      <h2 className="card-title">New invitation</h2>
      <p className="text-xs text-ink-300 -mt-2">
        {isPlatformAdmin
          ? "Pick scope = tenant and choose a tenant to invite a tenant user; pick scope = platform to invite another platform admin."
          : "Invitations you create are scoped to your tenant. Invitee receives a single-use link."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-200 inline-flex items-center">Role<InfoTip {...TIPS.invRole} /></span>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        {isPlatformAdmin && (
          <>
            <label className="block space-y-1">
              <span className="text-sm text-ink-200 inline-flex items-center">Scope<InfoTip {...TIPS.invScope} /></span>
              <select
                className="input"
                value={scope}
                onChange={(e) => setScope(e.target.value as Scope)}
              >
                <option value="platform">platform</option>
                <option value="tenant">tenant</option>
              </select>
            </label>
            {scope === "tenant" && (
              <label className="block space-y-1">
                <span className="text-sm text-ink-200 inline-flex items-center">Tenant<InfoTip {...TIPS.tenant} /></span>
                <TenantCombobox
                  tenants={tenants}
                  value={tenantId}
                  onChange={setTenantId}
                  loading={tenantsQuery.isLoading}
                  placeholder={
                    tenants.length === 0 && !tenantsQuery.isLoading
                      ? "No tenants — create one first"
                      : "Search tenants by name or slug…"
                  }
                />
                {tenantsQuery.error && (
                  <span className="text-xs text-severity-high">
                    Could not load tenants:{" "}
                    {tenantsQuery.error instanceof ApiError
                      ? tenantsQuery.error.message
                      : "error"}
                  </span>
                )}
              </label>
            )}
          </>
        )}
      </div>

      {error && <p className="text-sm text-severity-high">{error}</p>}

      <button type="submit" className="btn-primary" disabled={pending}>
        {pending ? "Creating…" : "Create invitation"}
      </button>
    </form>
  );
}

interface CreatedBannerProps {
  created: InvitationCreated;
  onDismiss: () => void;
}

function CreatedBanner({ created, onDismiss }: CreatedBannerProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(created.accept_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable in some browsers; user can still select+copy
    }
  }

  return (
    <section className="card border-l-4 border-severity-low">
      <div className="flex items-baseline justify-between">
        <h2 className="card-title">Invitation created</h2>
        <button type="button" className="text-ink-300 text-sm" onClick={onDismiss}>Dismiss</button>
      </div>
      <p className="text-sm text-ink-200 mb-2">
        Sent to <strong>{created.invitation.email}</strong>{" "}
        {created.email_sent ? (
          <span className="pill-ok ml-1">email sent</span>
        ) : (
          <span className="pill-medium ml-1">share link manually</span>
        )}
      </p>
      <div className="flex gap-2 items-center">
        <input className="input font-mono text-xs" readOnly value={created.accept_url} />
        <button type="button" className="btn-primary text-xs" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {created.email_error && (
        <p className="text-xs text-ink-400 mt-2">
          Email send failed: <code>{created.email_error}</code>. The link above still works.
        </p>
      )}
      <p className="text-xs text-ink-400 mt-2">
        This is the only time the link will be shown. Save it now if you need to resend.
      </p>
    </section>
  );
}
